<template>
  <div class="legal-page">
    <LegalPageHeader />

    <div class="legal-shell">
      <main class="legal-main">
        <div class="legal-title-block">
          <p class="legal-eyebrow">CuraScript</p>
          <h1 class="legal-page-title legal-page-title--long">CuraScript Service Level Agreement: Terms &amp; Conditions</h1>
          <p class="legal-lede">
            Agreement between CuraScript (Pty) Ltd (&ldquo;CuraScript&rdquo;) and the practitioner (&ldquo;Client&rdquo;).
          </p>
        </div>

        <b-card class="legal-card" no-body>
          <div class="legal-card-body">
            <article class="legal-article">
              <h2>1. Definitions</h2>
              <p>For the purpose of this Agreement, unless the context indicates otherwise:</p>
              <ul>
                <li><strong>&ldquo;Agreement&rdquo;</strong> means this document together with any annexures or attachments</li>
                <li>
                  <strong>&ldquo;Application&rdquo;</strong> means the CuraScript web and mobile software platform, including all
                  updates and enhancements
                </li>
                <li>
                  <strong>&ldquo;Business Day&rdquo;</strong> means any day other than Saturday, Sunday, or public holidays in the
                  Republic of South Africa
                </li>
                <li>
                  <strong>&ldquo;Claim&rdquo;</strong> means a single prescription generated and shared with a patient via the
                  Application
                </li>
                <li><strong>&ldquo;Commencement Date&rdquo;</strong> means the date the Client starts using CuraScript</li>
                <li><strong>&ldquo;Client&rdquo;</strong> means the licensed medical practitioner using CuraScript</li>
                <li>
                  <strong>&ldquo;Service&rdquo;</strong> means prescription creation, payment facilitation, and distribution
                  services provided by CuraScript
                </li>
                <li>
                  <strong>&ldquo;Intellectual Property&rdquo;</strong> means all rights related to CuraScript, including software,
                  branding, and systems
                </li>
                <li><strong>&ldquo;License&rdquo;</strong> means the right granted to the Client to use the Application</li>
                <li><strong>&ldquo;Fees&rdquo;</strong> means any applicable charges for use of the Service</li>
                <li>
                  <strong>&ldquo;User Guide Documentation&rdquo;</strong> means any manuals or instructions provided for use of
                  the Application
                </li>
              </ul>

              <h2>2. Introduction</h2>
              <p>
                CuraScript is a digital platform that enables practitioners to create, share, and manage prescriptions,
                facilitate payments, and communicate with patients and pharmacies.
              </p>
              <p>CuraScript agrees to provide the Services to the Client under the terms set out in this Agreement.</p>

              <h2>3. License to Use the Application</h2>
              <p>
                CuraScript grants the Client a limited, non-exclusive, non-transferable license to use the Application
                for the duration of this Agreement.
              </p>
              <p>Access to the Application may be subject to verification of the Client&rsquo;s credentials and professional licensing.</p>

              <h2>4. Duration and Termination</h2>
              <p>This Agreement will commence on the Commencement Date and continue indefinitely unless terminated.</p>
              <p>Either Party may terminate this Agreement by providing 30 (thirty) days&rsquo; written notice.</p>
              <p class="mb-1">Upon termination:</p>
              <ul>
                <li>The Client must stop using the Application immediately</li>
                <li>CuraScript may retain system data as required for legal or operational purposes</li>
              </ul>

              <h2>5. Fees and Payments</h2>
              <ul>
                <li>CuraScript may charge a fee per successful prescription or transaction</li>
                <li>Payments are processed through third-party providers</li>
                <li>CuraScript may deduct applicable fees from transactions before settlement</li>
                <li>Payment cycles (e.g., payouts to practitioners) may be processed periodically (e.g., every 14 days)</li>
                <li>All fees are exclusive of applicable taxes</li>
                <li>CuraScript reserves the right to revise fees with prior notice.</li>
              </ul>

              <h2>6. License Restrictions</h2>
              <p class="mb-1">The Client agrees not to:</p>
              <ul>
                <li>Copy, reproduce, modify, or distribute the Application</li>
                <li>Reverse engineer or attempt to extract source code</li>
                <li>Create derivative works based on the Application</li>
                <li>Provide access to unauthorized third parties</li>
              </ul>

              <h2>7. Client&rsquo;s Rights and Obligations</h2>
              <p class="mb-1">The Client agrees to:</p>
              <ul>
                <li>Use the Application in compliance with applicable medical laws and regulations</li>
                <li>Maintain confidentiality of patient data and access credentials</li>
                <li>Ensure prescriptions are accurate and lawful</li>
                <li>Use the platform only for professional medical purposes</li>
                <li>Maintain necessary hardware, software, and internet connectivity</li>
              </ul>
              <p class="mb-1">The Client must not:</p>
              <ul>
                <li>Share login credentials</li>
                <li>Misuse the platform or violate patient privacy</li>
              </ul>

              <h2>8. Support Services</h2>
              <p class="mb-1">CuraScript will provide support services, including:</p>
              <ul>
                <li>Email and/or helpdesk support</li>
                <li>Assistance with technical issues</li>
              </ul>
              <p>Support hours may be limited to standard business hours unless otherwise agreed.</p>

              <h2>9. CuraScript&rsquo;s Rights and Obligations</h2>
              <p class="mb-1">CuraScript shall not be responsible for issues arising from:</p>
              <ul>
                <li>Device or hardware failure</li>
                <li>Third-party software or services</li>
                <li>Internet connectivity issues</li>
                <li>Misuse or negligence by the Client</li>
              </ul>
              <p class="mb-1">CuraScript reserves the right to suspend access in case of:</p>
              <ul>
                <li>Security risks</li>
                <li>Suspected misuse</li>
                <li>Legal or regulatory requirements</li>
              </ul>

              <h2>10. Warranties and Indemnity</h2>
              <p>The Application is provided &ldquo;as is&rdquo; without warranties of any kind.</p>
              <p class="mb-1">The Client agrees to indemnify CuraScript against any claims, damages, or losses arising from:</p>
              <ul>
                <li>Misuse of the platform</li>
                <li>Incorrect prescriptions</li>
                <li>Breach of this Agreement</li>
              </ul>
              <p class="mb-1">CuraScript is not liable for:</p>
              <ul>
                <li>Medical outcomes</li>
                <li>Loss of revenue or data</li>
                <li>Indirect or consequential damages</li>
              </ul>

              <h2>11. Intellectual Property</h2>
              <p>All Intellectual Property related to CuraScript remains the exclusive property of CuraScript.</p>
              <p>The Client is granted only a limited right to use the Application under this Agreement.</p>

              <h2>12. Breach</h2>
              <p>
                If either Party breaches this Agreement and fails to remedy the breach within 21 (twenty-one) days of
                written notice:
              </p>
              <ul>
                <li>The other Party may terminate the Agreement, or</li>
                <li>Claim specific performance or damages</li>
              </ul>

              <h2>13. Third-Party Services</h2>
              <p class="mb-1">CuraScript may integrate with third-party services such as:</p>
              <ul>
                <li>Payment gateways</li>
                <li>Email or messaging platforms</li>
              </ul>
              <p>CuraScript is not responsible for the performance or policies of these third-party services.</p>

              <h2>14. Force Majeure</h2>
              <p class="mb-1">Neither Party shall be liable for failure to perform due to events beyond their control, including:</p>
              <ul>
                <li>Natural disasters</li>
                <li>War, strikes, or government actions</li>
                <li>System-wide failures</li>
              </ul>
              <p>Obligations will be suspended for the duration of such events.</p>

              <h2>15. Governing Law</h2>
              <p>This Agreement shall be governed by the laws of the Republic of South Africa.</p>

              <h2>16. Notices</h2>
              <p>All notices must be sent via email or written communication.</p>
              <p class="mb-0">
                <strong>CuraScript contact:</strong>
                Email:
                <a href="mailto:info@curascript.co.za" class="legal-link">info@curascript.co.za</a>
              </p>

              <h2>17. Relationship Between Parties</h2>
              <p>
                Nothing in this Agreement creates a partnership, joint venture, or agency relationship between CuraScript
                and the Client.
              </p>

              <h2>18. General</h2>
              <ul>
                <li>This Agreement constitutes the entire agreement between the Parties</li>
                <li>Any changes must be made in writing</li>
                <li>If any clause is found invalid, the remaining provisions remain enforceable</li>
                <li>Failure to enforce any provision does not constitute a waiver</li>
              </ul>

              <h2>19. Acceptance</h2>
              <p>
                By using CuraScript, the Client acknowledges that they have read, understood, and agreed to this Agreement.
              </p>
              <p class="mb-0">
                CuraScript is a technology platform that does not provide medical advice. All prescriptions and treatments
                are the sole responsibility of the licensed practitioner.
              </p>
            </article>
          </div>
        </b-card>
      </main>
    </div>

    <LegalPageFooter active="terms" />
  </div>
</template>

<script>
import LegalPageHeader from '@/components/legal/LegalPageHeader.vue';
import LegalPageFooter from '@/components/legal/LegalPageFooter.vue';

const PAGE_TITLE = 'Terms & Conditions | CuraScript';

export default {
  name: 'TermsAndConditions',
  components: {
    LegalPageHeader,
    LegalPageFooter,
  },
  data() {
    return {
      previousDocumentTitle: '',
    };
  },
  mounted() {
    this.previousDocumentTitle = document.title;
    document.title = PAGE_TITLE;
  },
  beforeDestroy() {
    document.title = this.previousDocumentTitle || 'Cura Script';
  },
};
</script>

<style lang="scss" scoped>
@import '../assets/styles/abstracts/_variables';

.legal-page-title--long {
  font-size: 1.35rem;
  @media (min-width: 576px) {
    font-size: 1.65rem;
  }
  @media (min-width: 768px) {
    font-size: 1.95rem;
  }
}
</style>
